import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import { GoPlus } from "react-icons/go";
import { useNavigate } from 'react-router-dom';
import { Can } from '../../../Components/Can';
import { Button } from '../../../Components/Form/Button/button.scomponent';
import { ButtonActionMenu } from '../../../Components/Form/ButtonActionsMenu/button-action-menu.component';
import { TableListSkeleton } from '../../../Components/Table/table-list-skeleton.component';
import { Placeholder } from '../../../Components/Placeholder/placeholder.component';
import { Table } from '../../../Components/Table/table.component';
import { BaseContentPage } from '../../../Components/Templates/BaseContentPage/base-content-page.componen';
import { BaseScreen } from '../../../Components/Templates/BaseScreen/basescreen.component';
import { ListSellersUseCase } from '../../../Usecase/Sellers/list-sellers-usecase';
import { useListSellers } from './sellers-list.hook';
import { DestroySellersUseCase } from '../../../Usecase/Sellers/destroy-sellers-usecase';
import { ConfirmDialog } from '../../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component';

interface ISellersList {
    listSellersUseCase: ListSellersUseCase
    destroySellersUseCase: DestroySellersUseCase
}

const CellText: FC<{ value?: string | null }> = ({ value }) => {
  const text = value?.trim() || '—'

  return (
    <Tooltip title={text !== '—' ? text : ''} placement="top-start" enterDelay={400}>
      <Typography
        variant="body2"
        component="span"
        sx={{
          display: 'block',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.25,
          fontSize: '0.8125rem',
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  )
}

const SellersList : FC<ISellersList> = ({ listSellersUseCase, destroySellersUseCase }) => {
    const navigate = useNavigate()

    const {
      listSellers,
      list,
      loading,
      loadError,
      totalRegisters,
      handleSearch,
      handlePageChange,
      currentSearch,
      sellerToDestroy,
      sellerToDestroyName,
      setSellerToDestroy,
      handleDestroySeller,
    } = useListSellers({ listSellersUseCase, destroySellersUseCase })

    useEffect(() => {
      listSellers({ page: 1, search: '' })
    }, [listSellers])

    const columns = useMemo(() => {
        const createColumns: any[] = [
          {
            field: 'name',
            headerName: 'Nome',
            flex: 1,
            minWidth: 160,
            sortable: true,
            renderCell: (params: any) => <CellText value={params.row?.name} />,
          },
          {
            field: 'email',
            headerName: 'E-mail',
            flex: 1.2,
            minWidth: 220,
            sortable: true,
            renderCell: (params: any) => <CellText value={params.row?.email} />,
          },
          {
            field: "actions",
            headerName: "Ações",
            headerAlign: "center",
            align: "center",
            width: 72,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: any) => (
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <ButtonActionMenu options={[
                  {
                    title: 'Editar',
                    onClick: () => navigate(`/sellers-form/${params.row.id}`),
                    icon: <EditIcon fontSize="small" />,
                  },
                  {
                    title: 'Excluir',
                    onClick: () => setSellerToDestroy(params.row.id),
                    icon: <DeleteOutlineIcon fontSize="small" />,
                    titleColor: '#d32f2f',
                  },
                ]} />
              </Box>
            )
          }
        ];
      
        return createColumns;
      }, [navigate]);

    return (
      <Can permissions={['menu:sellers:manage']}>  
        <BaseScreen>
          <ConfirmDialog
            title='Remover Vendedor'
            onCancel={() => setSellerToDestroy('')}
            description={`Tem certeza que deseja remover "${sellerToDestroyName}"? Essa ação remove o vendedor apenas da base do HUB.`}
            open={!!sellerToDestroy}
            onConfirm={handleDestroySeller}
          />
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <BaseContentPage 
                    title='Vendedores'
                    subtitle='Gerencie os vendedores cadastrados no HUB'
                    breadcrumb={[{ title: 'Vendedores', link: "/sellers-list"}, { title: 'Listagem'}]}
                    minHeight="auto"
                    search
                    searchFunction={handleSearch}
                    searchLoading={loading && !!list?.length}
                    placeholderSearch='Pesquisar por nome ou e-mail'
                    Action={() => (
                      <Button
                        LeftIcon={() => <GoPlus size={18} />}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 130 }, flexShrink: 0 }}
                        boxLeftSx={{ gap: '10px', marginLeft: '5px' }}
                        textSx={{ textTransform: 'Capitalize', fontWeight: 500, whiteSpace: 'nowrap' }}
                        onClick={() => navigate('/sellers-form')}
                      >
                        Adicionar
                      </Button>
                    )}
                >
                  {!!list?.length ? (
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <Table
                        data={list}
                        columns={columns}
                        totalCount={totalRegisters}
                        search={currentSearch}
                        onPageChange={handlePageChange}
                        sortingMode="client"
                        rowHeight={52}
                        stickyHeader
                        maxHeight="calc(100vh - 280px)"
                      />
                    </Box>
                  ) : loading ? (
                    <TableListSkeleton rowHeight={52} />
                  ) : loadError ? (
                    <Placeholder
                      description="Não foi possível carregar os vendedores."
                      onRetry={() => listSellers({ page: 1, search: currentSearch })}
                    />
                  ) : (
                    <Placeholder description="Nenhum vendedor encontrado." />
                  )}
                </BaseContentPage>
            </Paper>
        </BaseScreen>
        </Can>
    )
}

export { SellersList };
